/**
 * The Lab Orders page keeps its tab, filters, sort and page in the URL so a
 * view can be bookmarked or shared. These convert between the URL and the
 * API's query (GET client/labs/orders).
 */

export type OrdersTab = 'cases' | 'accounts'
export const PAGE_SIZES = [25, 50, 100] as const
export const STATUSES = ['Pending', 'Accepted', 'In Progress', 'On Hold', 'Completed', 'Cancelled'] as const
export const PAYMENTS = ['Unpaid', 'Partial', 'Settled'] as const
export type SortKey = 'orderDate' | 'dueDate' | 'status' | 'lab' | 'price'

export type OrdersFilters = {
  tab: OrdersTab
  /** YYYY-MM, or 'all' */
  month: string
  categoryIds: string[]
  catalogItemIds: string[]
  labPartnerId: string
  status: string[]
  urgent: boolean
  payment: string[]
  dentistId: string
  q: string
  /** e.g. "-orderDate" */
  sort: string
  page: number
  limit: number
}

export function currentMonth(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

const list = (v: string | null) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : [])

export function filtersFromSearch(sp: URLSearchParams, now = new Date()): OrdersFilters {
  const month = sp.get('month')
  const limit = Number(sp.get('limit'))
  return {
    tab: sp.get('tab') === 'accounts' ? 'accounts' : 'cases',
    month: month === 'all' || /^\d{4}-\d{2}$/.test(month || '') ? (month as string) : currentMonth(now),
    categoryIds: list(sp.get('categoryIds')),
    catalogItemIds: list(sp.get('catalogItemIds')),
    labPartnerId: sp.get('labPartnerId') || '',
    status: list(sp.get('status')).filter((s) => (STATUSES as readonly string[]).includes(s)),
    urgent: sp.get('priority') === 'Urgent',
    payment: list(sp.get('payment')).filter((s) => (PAYMENTS as readonly string[]).includes(s)),
    dentistId: sp.get('dentistId') || '',
    q: sp.get('q') || '',
    sort: sp.get('sort') || '-orderDate',
    page: Math.max(1, Number(sp.get('page')) || 1),
    limit: (PAGE_SIZES as readonly number[]).includes(limit) ? limit : 25,
  }
}

/** The URL for a set of filters; defaults are left out so URLs stay short. */
export function filtersToSearch(f: OrdersFilters, now = new Date()): URLSearchParams {
  const sp = new URLSearchParams()
  if (f.tab !== 'cases') sp.set('tab', f.tab)
  if (f.month !== currentMonth(now)) sp.set('month', f.month)
  if (f.categoryIds.length) sp.set('categoryIds', f.categoryIds.join(','))
  if (f.catalogItemIds.length) sp.set('catalogItemIds', f.catalogItemIds.join(','))
  if (f.labPartnerId) sp.set('labPartnerId', f.labPartnerId)
  if (f.status.length) sp.set('status', f.status.join(','))
  if (f.urgent) sp.set('priority', 'Urgent')
  if (f.payment.length) sp.set('payment', f.payment.join(','))
  if (f.dentistId) sp.set('dentistId', f.dentistId)
  if (f.q.trim()) sp.set('q', f.q.trim())
  if (f.sort !== '-orderDate') sp.set('sort', f.sort)
  if (f.page > 1) sp.set('page', String(f.page))
  if (f.limit !== 25) sp.set('limit', String(f.limit))
  return sp
}

/** Query for the API (and the Excel export, which ignores page/limit). */
export function apiParams(f: OrdersFilters): Record<string, string | number> {
  const p: Record<string, string | number> = { page: f.page, limit: f.limit, month: f.month, sort: f.sort, tab: f.tab }
  if (f.categoryIds.length) p.categoryIds = f.categoryIds.join(',')
  if (f.catalogItemIds.length) p.catalogItemIds = f.catalogItemIds.join(',')
  if (f.labPartnerId) p.labPartnerId = f.labPartnerId
  if (f.status.length) p.status = f.status.join(',')
  if (f.urgent) p.priority = 'Urgent'
  if (f.payment.length && f.tab === 'accounts') p.payment = f.payment.join(',')
  if (f.dentistId) p.dentistId = f.dentistId
  if (f.q.trim()) p.q = f.q.trim()
  return p
}

export function shiftMonth(month: string, delta: number) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export function monthLabel(month: string) {
  if (month === 'all') return 'All time'
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

/** Clicking a column: same key flips the direction, a new key starts descending for dates and price. */
export function nextSort(current: string, key: SortKey) {
  const desc = current.startsWith('-')
  const now = desc ? current.slice(1) : current
  if (now === key) return desc ? key : `-${key}`
  return key === 'status' || key === 'lab' ? key : `-${key}`
}
